import { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import OtpInput from '../../components/common/OtpInput';
import Button from '../../components/common/Button';

const ROLES = [
  { value: 'worker',   emoji: '👷', label: 'Worker',               sub: 'Find Jobs & Earn' },
  { value: 'employer', emoji: '🏢', label: 'Employer / Contractor', sub: 'Hire Skilled Workers' },
];

function redirect(role) {
  return role === 'admin' || role === 'superadmin' ? '/admin/dashboard' : `/${role}/dashboard`;
}

export default function Login() {
  const navigate    = useNavigate();
  const setAuth     = useAuthStore((s) => s.setAuth);
  const user        = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);

  // OTP login flow
  const [step, setStep]           = useState('mobile'); // 'mobile' | 'otp'
  const [mobile, setMobile]       = useState('');
  const [otp, setOtp]             = useState('');
  const [countdown, setCountdown] = useState(0);

  // Shared
  const [role, setRole]           = useState('worker');
  const [loginMode, setLoginMode] = useState('otp'); // 'otp' | 'email' | 'reset'
  const [loading, setLoading]     = useState(false);

  // Email/password login
  const [emailVal, setEmailVal]   = useState('');
  const [emailPass, setEmailPass] = useState('');
  const [showPass, setShowPass]   = useState(false);

  // Reset password flow
  const [resetStep, setResetStep]           = useState('email'); // 'email' | 'otp' | 'newpass'
  const [resetEmail, setResetEmail]         = useState('');
  const [resetOtp, setResetOtp]             = useState('');
  const [resetCountdown, setResetCountdown] = useState(0);
  const [newPass, setNewPass]               = useState('');
  const [confirmPass, setConfirmPass]       = useState('');
  const [showNewPass, setShowNewPass]       = useState(false);

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  useEffect(() => {
    if (resetCountdown > 0) {
      const t = setTimeout(() => setResetCountdown((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resetCountdown]);

  function selectRole(r) {
    setRole(r);
    setLoginMode(r === 'admin' ? 'email' : 'otp');
    setStep('mobile');
  }

  function toggleAdmin() {
    selectRole(role === 'admin' ? 'worker' : 'admin');
  }

  function enterReset() {
    setResetStep('email');
    setResetEmail('');
    setResetOtp('');
    setNewPass('');
    setConfirmPass('');
    setLoginMode('reset');
  }

  function exitReset() {
    setLoginMode(role === 'admin' ? 'email' : 'otp');
  }

  // ── OTP login handlers ──
  async function handleSendOtp(e) {
    e?.preventDefault();
    if (!/^[6-9]\d{9}$/.test(mobile)) return toast.error('Enter a valid 10-digit Indian mobile number');
    setLoading(true);
    try {
      const res = await api.post('/auth/send-otp', { mobile, purpose: 'login' });
      setStep('otp');
      setCountdown(60);
      if (res.data?.data?.dev_otp) {
        toast.success(`[DEV] OTP: ${res.data.data.dev_otp}`, { duration: 30000 });
      } else {
        toast.success(`OTP sent to +91 ${mobile}`);
      }
    } catch {}
    finally { setLoading(false); }
  }

  async function handleVerifyOtp(e) {
    e.preventDefault();
    if (otp.length !== 6) return toast.error('Enter the 6-digit OTP');
    setLoading(true);
    try {
      const res = await api.post('/auth/login', { mobile, otp, role });
      const { accessToken: token, user: u } = res.data.data;
      setAuth(u, token);
      toast.success('Welcome to naukarihaat.com!');
      navigate(redirect(u.role));
    } catch {}
    finally { setLoading(false); }
  }

  // ── Email login handler ──
  async function handleEmailLogin(e) {
    e.preventDefault();
    if (!emailVal.trim()) return toast.error('Enter your email address');
    if (!emailPass) return toast.error('Enter your password');
    setLoading(true);
    try {
      const res = await api.post('/auth/login/email', { email: emailVal.trim(), password: emailPass });
      const { accessToken: token, user: u } = res.data.data;
      setAuth(u, token);
      toast.success('Welcome to naukarihaat.com!');
      navigate(redirect(u.role));
    } catch {}
    finally { setLoading(false); }
  }

  // ── Reset password handlers ──
  async function handleResetSendOtp(e) {
    e?.preventDefault();
    if (!resetEmail.trim() || !/\S+@\S+\.\S+/.test(resetEmail)) return toast.error('Enter a valid email address');
    setLoading(true);
    try {
      const res = await api.post('/auth/forgot-password', { email: resetEmail.trim() });
      setResetStep('otp');
      setResetCountdown(60);
      if (res.data?.data?.dev_otp) {
        toast.success(`[DEV] OTP: ${res.data.data.dev_otp}`, { duration: 30000 });
      } else {
        toast.success(`OTP sent to ${resetEmail.trim()}`);
      }
    } catch {}
    finally { setLoading(false); }
  }

  function handleResetVerifyOtp(e) {
    e.preventDefault();
    if (resetOtp.length !== 6) return toast.error('Enter the 6-digit OTP');
    setResetStep('newpass');
  }

  async function handleResetPassword(e) {
    e.preventDefault();
    if (newPass.length < 8) return toast.error('Password must be at least 8 characters');
    if (newPass !== confirmPass) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { email: resetEmail.trim(), otp: resetOtp, password: newPass });
      toast.success('Password reset successfully! Please log in.');
      exitReset();
    } catch {}
    finally { setLoading(false); }
  }

  if (user && accessToken) {
    return <Navigate to={redirect(user.role)} replace />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-brand-800">
      {/* ── Branding header ── */}
      <div className="flex flex-col items-center pt-12 pb-8 px-6 text-white select-none">
        <div className="w-16 h-16 bg-white/15 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4 ring-2 ring-white/25 shadow-lg">
          <span className="text-3xl font-extrabold tracking-tight">NH</span>
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight">naukarihaat.com</h1>
        <p className="text-brand-200 mt-1.5 text-sm text-center">Your bridge to better work</p>
      </div>

      {/* ── Main card ── */}
      <div className="flex-1 bg-white rounded-t-[2rem] shadow-2xl overflow-auto">
        <div className="max-w-sm mx-auto px-6 pt-8 pb-10">

          {/* ── OTP flow: step=mobile ── */}
          {loginMode === 'otp' && step === 'mobile' && (
            <div className="space-y-6">
              {/* Role selector */}
              <div>
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest text-center mb-3">
                  I am a
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {ROLES.map(({ value, emoji, label, sub }) => (
                    <button key={value} type="button" onClick={() => selectRole(value)}
                      className={`flex flex-col items-center gap-1.5 py-4 px-2 rounded-2xl border-2 transition-all ${
                        role === value
                          ? 'border-brand-500 bg-brand-50 shadow-sm'
                          : 'border-gray-100 bg-gray-50 hover:border-gray-200'
                      }`}>
                      <span className="text-3xl leading-none">{emoji}</span>
                      <span className={`text-sm font-bold leading-tight text-center ${role === value ? 'text-brand-700' : 'text-gray-700'}`}>
                        {label}
                      </span>
                      <span className={`text-[11px] text-center ${role === value ? 'text-brand-500' : 'text-gray-400'}`}>
                        {sub}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Admin toggle */}
                <button type="button" onClick={toggleAdmin}
                  className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border text-xs font-medium transition-all border-dashed border-gray-300 text-gray-400 hover:border-gray-400 hover:text-gray-500">
                  <span>🔐</span>
                  <span>Admin / Staff Login</span>
                </button>
              </div>

              <form onSubmit={handleSendOtp} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="block text-sm font-semibold text-gray-700">Mobile Number</label>
                  <div className="flex rounded-xl border-2 border-gray-200 bg-white overflow-hidden transition-colors focus-within:border-brand-500">
                    <div className="flex items-center justify-center px-4 bg-gray-50 border-r-2 border-gray-200 shrink-0">
                      <span className="text-sm font-bold text-gray-600 select-none">+91</span>
                    </div>
                    <input
                      type="tel" inputMode="numeric" placeholder="9876543210"
                      value={mobile} maxLength={10}
                      onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className="flex-1 px-3.5 py-3 text-base text-gray-900 bg-transparent placeholder-gray-400 focus:outline-none"
                      required autoFocus
                    />
                  </div>
                </div>

                <Button type="submit" fullWidth size="lg" loading={loading}>
                  Get OTP →
                </Button>
              </form>

              <div className="text-center space-y-2">
                <button type="button"
                  onClick={() => setLoginMode('email')}
                  className="text-xs text-brand-600 hover:text-brand-800 font-medium underline underline-offset-2">
                  Login with email &amp; password instead
                </button>
                <p>
                  <button type="button" onClick={enterReset}
                    className="text-xs text-gray-500 hover:text-gray-700 underline underline-offset-2">
                    Forgot / Reset Password
                  </button>
                </p>
              </div>
            </div>
          )}

          {/* ── OTP flow: step=otp ── */}
          {loginMode === 'otp' && step === 'otp' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-14 h-14 bg-brand-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">📱</span>
                </div>
                <h2 className="text-xl font-bold text-gray-900">Enter OTP</h2>
                <p className="text-sm text-gray-500 mt-1">
                  6-digit code sent to{' '}
                  <span className="font-semibold text-gray-800">+91 {mobile}</span>
                </p>
                <button type="button"
                  onClick={() => { setStep('mobile'); setOtp(''); }}
                  className="text-xs text-brand-600 hover:text-brand-800 underline underline-offset-2 mt-1.5">
                  ← Change number
                </button>
              </div>

              <form onSubmit={handleVerifyOtp} className="space-y-5">
                <OtpInput length={6} onChange={setOtp} />
                <Button type="submit" fullWidth size="lg" loading={loading}>
                  Verify &amp; Login
                </Button>
                <div className="text-center text-sm">
                  {countdown > 0 ? (
                    <span className="text-gray-400">
                      Resend OTP in <span className="font-bold text-gray-700">{countdown}s</span>
                    </span>
                  ) : (
                    <button type="button" onClick={handleSendOtp}
                      className="text-brand-600 hover:text-brand-800 font-semibold underline underline-offset-2">
                      Resend OTP
                    </button>
                  )}
                </div>
              </form>
            </div>
          )}

          {/* ── Email + Password login ── */}
          {loginMode === 'email' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3 ${role === 'admin' ? 'bg-gray-100' : 'bg-brand-50'}`}>
                  <span className="text-2xl">{role === 'admin' ? '🔐' : '✉️'}</span>
                </div>
                <h2 className="text-xl font-bold text-gray-900">
                  {role === 'admin' ? 'Admin / Staff Login' : 'Email Login'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {role === 'admin'
                    ? 'Sign in with your admin email and password'
                    : 'Sign in with your registered email and password'}
                </p>
              </div>

              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="block text-sm font-semibold text-gray-700">Email Address</label>
                  <input
                    type="email" placeholder="you@example.com"
                    value={emailVal}
                    onChange={(e) => setEmailVal(e.target.value)}
                    className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:border-brand-500 transition-colors"
                    required autoFocus
                  />
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-semibold text-gray-700">Password</label>
                    <button type="button" onClick={enterReset}
                      className="text-xs text-brand-600 hover:text-brand-800 underline underline-offset-2">
                      Forgot Password?
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={showPass ? 'text' : 'password'}
                      placeholder="Your password"
                      value={emailPass}
                      onChange={(e) => setEmailPass(e.target.value)}
                      className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 pr-12 text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:border-brand-500 transition-colors"
                      required
                    />
                    <button type="button" tabIndex={-1}
                      onClick={() => setShowPass((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm select-none">
                      {showPass ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </div>

                <Button type="submit" fullWidth size="lg" loading={loading}>
                  Sign In →
                </Button>
              </form>

              <div className="text-center space-y-2">
                {role === 'admin' ? (
                  <button type="button"
                    onClick={() => selectRole('worker')}
                    className="text-xs text-gray-500 hover:text-gray-700 underline underline-offset-2">
                    ← Back to regular login
                  </button>
                ) : (
                  <button type="button"
                    onClick={() => setLoginMode('otp')}
                    className="text-xs text-brand-600 hover:text-brand-800 font-medium underline underline-offset-2">
                    ← Login with mobile OTP instead
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── Reset Password flow ── */}
          {loginMode === 'reset' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <span className="text-2xl">🔑</span>
                </div>
                <h2 className="text-xl font-bold text-gray-900">Reset Password</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {resetStep === 'email'   && 'Enter your registered email address'}
                  {resetStep === 'otp'     && `OTP sent to ${resetEmail}`}
                  {resetStep === 'newpass' && 'Set your new password'}
                </p>
              </div>

              {/* Step 1 — Email */}
              {resetStep === 'email' && (
                <form onSubmit={handleResetSendOtp} className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="block text-sm font-semibold text-gray-700">Email Address</label>
                    <input
                      type="email" placeholder="you@example.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:border-brand-500 transition-colors"
                      required autoFocus
                    />
                  </div>
                  <Button type="submit" fullWidth size="lg" loading={loading}>
                    Send OTP →
                  </Button>
                </form>
              )}

              {/* Step 2 — OTP */}
              {resetStep === 'otp' && (
                <form onSubmit={handleResetVerifyOtp} className="space-y-5">
                  <p className="text-xs text-center text-gray-500">
                    Sent to <span className="font-semibold text-gray-700">{resetEmail}</span>
                    {' · '}
                    <button type="button" onClick={() => setResetStep('email')}
                      className="text-brand-600 hover:text-brand-800 underline underline-offset-2">
                      Change
                    </button>
                  </p>
                  <OtpInput length={6} onChange={setResetOtp} />
                  <Button type="submit" fullWidth size="lg" loading={loading}>
                    Verify OTP →
                  </Button>
                  <div className="text-center text-sm">
                    {resetCountdown > 0 ? (
                      <span className="text-gray-400">
                        Resend in <span className="font-bold text-gray-700">{resetCountdown}s</span>
                      </span>
                    ) : (
                      <button type="button" onClick={handleResetSendOtp}
                        className="text-brand-600 hover:text-brand-800 font-semibold underline underline-offset-2">
                        Resend OTP
                      </button>
                    )}
                  </div>
                </form>
              )}

              {/* Step 3 — New password */}
              {resetStep === 'newpass' && (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="block text-sm font-semibold text-gray-700">New Password</label>
                    <div className="relative">
                      <input
                        type={showNewPass ? 'text' : 'password'}
                        placeholder="Min 8 characters"
                        value={newPass}
                        onChange={(e) => setNewPass(e.target.value)}
                        className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 pr-12 text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:border-brand-500 transition-colors"
                        required autoFocus
                      />
                      <button type="button" tabIndex={-1}
                        onClick={() => setShowNewPass((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm select-none">
                        {showNewPass ? 'Hide' : 'Show'}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-sm font-semibold text-gray-700">Confirm Password</label>
                    <input
                      type="password"
                      placeholder="Repeat new password"
                      value={confirmPass}
                      onChange={(e) => setConfirmPass(e.target.value)}
                      className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:border-brand-500 transition-colors"
                      required
                    />
                  </div>
                  <Button type="submit" fullWidth size="lg" loading={loading}>
                    Reset Password →
                  </Button>
                </form>
              )}

              <div className="text-center">
                <button type="button" onClick={exitReset}
                  className="text-xs text-gray-500 hover:text-gray-700 underline underline-offset-2">
                  ← Back to login
                </button>
              </div>
            </div>
          )}

          <p className="text-xs text-gray-400 text-center mt-8">
            By continuing you agree to our Terms of Service &amp; Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
}
